import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import toast from "react-hot-toast";
import EmsFeeReceiptDocument from "./EmsFeeReceiptDocument";
import { fetchFeeReceiptData } from "./feeReceiptUtils";
import { downloadEmsFeeReceiptPdf } from "./feeReceiptPdf";
import usePermissionDataStore from "../../../Zustand/Store/usePermissionDataStore";
import { hasPermissionAction } from "../../../Utils/organizationPermissions";

function Reciept() {
  const [searchParams] = useSearchParams();
  const studentFeeId = searchParams.get("student_fee_id");
  const organizationId = searchParams.get("organization_id");
  const { Permission } = usePermissionDataStore();
  const canDownloadFeeReceipt = useMemo(
    () => hasPermissionAction(Permission, "DOWNLOAD_FEE_RECEIPT"),
    [Permission]
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [receiptData, setReceiptData] = useState(null);

  useEffect(() => {
    if (!studentFeeId) {
      setReceiptData(null);
      setError("Missing student_fee_id in URL.");
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError("");

    fetchFeeReceiptData(studentFeeId, organizationId)
      .then((data) => {
        if (cancelled) return;
        if (!data) {
          setError("Could not load receipt data.");
          setReceiptData(null);
          return;
        }
        setReceiptData(data);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Fee receipt fetch failed:", err);
        setError("Could not load receipt data.");
        setReceiptData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [studentFeeId, organizationId]);

  const handleDownload = () => {
    if (!canDownloadFeeReceipt) {
      toast.error("You do not have permission to download fee receipts.");
      return;
    }
    if (!receiptData) return;
    const safe = String(receiptData.receiptNo).replace(/[^\w.-]+/g, "_");
    downloadEmsFeeReceiptPdf(receiptData, `fee-receipt-${safe}.pdf`);
  };

  return (
    <Box sx={{ py: 2, px: { xs: 1, md: 2 }, background: "#f3f4f6", minHeight: "100vh" }}>
      <Box
        sx={{
          maxWidth: 900,
          mx: "auto",
          mb: 2,
          display: "flex",
          gap: 1,
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
        }}
      >
        <Typography variant="h6">Fee Receipt</Typography>
        {canDownloadFeeReceipt ? (
          <Button
            variant="contained"
            onClick={handleDownload}
            disabled={!receiptData || loading}
          >
            Download PDF
          </Button>
        ) : null}
      </Box>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Typography color="error" align="center" sx={{ py: 4 }}>
          {error}
        </Typography>
      )}

      {!loading && receiptData && <EmsFeeReceiptDocument data={receiptData} />}
    </Box>
  );
}

export default Reciept;
